import { Injectable, signal } from '@angular/core';
import { findRoute, RoleTier, WorkspaceRoute } from './marketing-workspace.routes';

const DEFAULT_PATH = 'home';

/**
 * Holds the current section path for one MarketingWorkspace dashboard instance.
 *
 * URL binding lives in the shell (which reads ?section= on init and writes it on nav).
 * This service is the in-memory state; URL sync is one-directional from the shell.
 *
 * Each dashboard instance gets its own injector so multiple open Marketing Workspace tabs
 * (e.g., one per company) each have independent active sections.
 */
@Injectable()
export class WorkspaceStateService {
    private readonly _path = signal<string>(DEFAULT_PATH);
    private readonly _selectedId = signal<string>('');
    /**
     * Active user's marketing role for the currently-selected company.
     * - `null` means "system / Owner-type user (full access)" or "not yet
     *   loaded". Sections that gate edit affordances should treat both as
     *   "show controls" — the route filter already kept under-privileged
     *   users out, and system users are intentionally unrestricted.
     * - `'Admin' | 'Operator' | 'Viewer'` is a known tier from
     *   `marketing.AuthorizedUser.Role`. Sections gate write affordances
     *   off this signal.
     *
     * The shell sets this when the active company changes (see
     * `MarketingWorkspaceComponent.loadUserRoleForCompany`).
     */
    private readonly _userRole = signal<RoleTier | null>(null);

    readonly path = this._path.asReadonly();
    readonly selectedId = this._selectedId.asReadonly();
    readonly userRole = this._userRole.asReadonly();

    setPath(path: string): void {
        const safe = findRoute(path) ? path : DEFAULT_PATH;
        this._path.set(safe);
    }

    setSelectedId(id: string): void {
        this._selectedId.set(id || '');
    }

    setUserRole(role: RoleTier | null): void {
        this._userRole.set(role);
    }

    currentRoute(): WorkspaceRoute | null {
        return findRoute(this._path());
    }
}
